import React, { useState, useEffect } from 'react';
import { ArrowRight, Play, Zap, Truck, Shield, Clock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

/**
 * Template 2: IMPULSE
 * Style: Bold, Energetic, Conversion-focused
 * Best for: Streetwear, Athletic wear, Sports brands
 * Inspired by: Shopify Impulse theme
 */
const Template2 = () => {
    const navigate = useNavigate();

    // Countdown Timer State
    const [timeLeft, setTimeLeft] = useState({
        hours: 23,
        minutes: 59,
        seconds: 59
    });

    useEffect(() => {
        const timer = setInterval(() => {
            setTimeLeft(prev => {
                if (prev.seconds > 0) {
                    return { ...prev, seconds: prev.seconds - 1 };
                } else if (prev.minutes > 0) {
                    return { ...prev, minutes: prev.minutes - 1, seconds: 59 };
                } else if (prev.hours > 0) {
                    return { hours: prev.hours - 1, minutes: 59, seconds: 59 };
                }
                return prev;
            });
        }, 1000);
        return () => clearInterval(timer);
    }, []);

    return (
        <div className="min-h-screen bg-black text-white">

            {/* ============================================
                ANNOUNCEMENT BAR - Flash Sale with countdown
            ============================================ */}
            <div className="bg-gradient-to-r from-red-600 via-orange-500 to-yellow-500 py-2 px-4">
                <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4 text-center">
                    <div className="flex items-center gap-2">
                        <Zap className="w-4 h-4 animate-pulse" />
                        <span className="font-bold text-sm uppercase tracking-wide">Flash Sale</span>
                    </div>
                    <span className="text-sm">Up to 50% Off - Ends in</span>
                    <div className="flex items-center gap-1 font-mono font-bold">
                        <span className="bg-black/20 px-2 py-0.5 rounded">{String(timeLeft.hours).padStart(2, '0')}</span>:
                        <span className="bg-black/20 px-2 py-0.5 rounded">{String(timeLeft.minutes).padStart(2, '0')}</span>:
                        <span className="bg-black/20 px-2 py-0.5 rounded">{String(timeLeft.seconds).padStart(2, '0')}</span>
                    </div>
                </div>
            </div>

            {/* ============================================
                HERO SECTION - Bold split design
            ============================================ */}
            <section className="relative min-h-[90vh] flex items-center overflow-hidden">
                {/* Background with gradient overlay */}
                <div className="absolute inset-0">
                    <img
                        src="https://images.unsplash.com/photo-1556906781-9a412961c28c?w=1920&q=80"
                        alt="Hero"
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-transparent"></div>
                </div>

                {/* Content */}
                <div className="relative w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
                    <div className="max-w-2xl">
                        <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-2 mb-6">
                            <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                            <span className="text-sm font-medium">New Drop Available</span>
                        </div>

                        <h1 className="text-5xl sm:text-6xl lg:text-8xl font-black uppercase leading-none mb-6">
                            <span className="block">Push</span>
                            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-red-500 via-orange-500 to-yellow-500">
                                Your Limits
                            </span>
                        </h1>

                        <p className="text-lg sm:text-xl text-gray-300 mb-8 max-w-lg">
                            Performance-driven streetwear engineered for athletes who never settle.
                            Designed to move with you, built to stand out.
                        </p>

                        <div className="flex flex-col sm:flex-row gap-4">
                            <button
                                onClick={() => navigate('/template2/shop')}
                                className="group inline-flex items-center justify-center gap-3 bg-white text-black px-8 py-4 font-bold uppercase tracking-wide hover:bg-gray-200 transition-all duration-300"
                            >
                                Shop New Arrivals
                                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                            </button>
                            <button className="inline-flex items-center justify-center gap-3 border-2 border-white/50 px-8 py-4 font-bold uppercase tracking-wide hover:bg-white/10 transition-all duration-300">
                                <Play className="w-5 h-5" />
                                Watch Film
                            </button>
                        </div>
                    </div>
                </div>

                {/* Floating Stats */}
                <div className="absolute bottom-8 right-8 hidden lg:flex flex-col gap-4">
                    {[
                        { value: '50K+', label: 'Athletes' },
                        { value: '4.9', label: 'Rating' },
                        { value: '100+', label: 'Countries' }
                    ].map((stat, index) => (
                        <div key={index} className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg px-6 py-3 text-center">
                            <p className="text-2xl font-black">{stat.value}</p>
                            <p className="text-xs text-gray-400 uppercase tracking-wider">{stat.label}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* ============================================
                CATEGORIES - Bold cards with hover effect
            ============================================ */}
            <section className="py-16 sm:py-20 lg:py-24 bg-zinc-950">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black uppercase mb-4">
                            Shop By Sport
                        </h2>
                        <p className="text-gray-400 text-lg">Find gear built for your game</p>
                    </div>

                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
                        {[
                            { name: 'Running', image: 'https://images.unsplash.com/photo-1571008887538-b36bb32f4571?w=500&h=600&fit=crop', count: '120+ Items' },
                            { name: 'Training', image: 'https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=500&h=600&fit=crop', count: '95+ Items' },
                            { name: 'Basketball', image: 'https://images.unsplash.com/photo-1546519638-68e109498ffc?w=500&h=600&fit=crop', count: '80+ Items' },
                            { name: 'Lifestyle', image: 'https://images.unsplash.com/photo-1552374196-1ab2a1c593e8?w=500&h=600&fit=crop', count: '200+ Items' }
                        ].map((category, index) => (
                            <div
                                key={index}
                                onClick={() => navigate('/template2/shop')}
                                className="group relative aspect-[3/4] overflow-hidden cursor-pointer"
                            >
                                <img
                                    src={category.image}
                                    alt={category.name}
                                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent opacity-80 group-hover:opacity-90 transition-opacity"></div>

                                {/* Category Info */}
                                <div className="absolute inset-0 flex flex-col justify-end p-4 lg:p-6">
                                    <p className="text-white/70 text-sm mb-1">{category.count}</p>
                                    <h3 className="text-white text-xl lg:text-2xl font-bold uppercase mb-2">{category.name}</h3>
                                    <div className="flex items-center gap-2 text-white text-sm font-medium opacity-0 group-hover:opacity-100 translate-y-4 group-hover:translate-y-0 transition-all duration-300">
                                        Shop Now <ArrowRight className="w-4 h-4" />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ============================================
                FEATURED PRODUCTS - Hot items with badges
            ============================================ */}
            <section className="py-16 sm:py-20 lg:py-24 bg-black">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-12">
                        <div>
                            <div className="inline-flex items-center gap-2 text-red-500 text-sm font-bold uppercase tracking-wider mb-2">
                                <Zap className="w-4 h-4" />
                                Trending Now
                            </div>
                            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black uppercase">
                                Hot Drops
                            </h2>
                        </div>
                        <button
                            onClick={() => navigate('/template2/shop')}
                            className="inline-flex items-center gap-2 text-white text-sm font-bold uppercase tracking-wide hover:text-red-500 transition-colors"
                        >
                            View All <ArrowRight className="w-4 h-4" />
                        </button>
                    </div>

                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
                        {[
                            { name: 'Pro Runner Elite', price: '₹8,999', originalPrice: '₹12,999', badge: 'SALE', image: 'https://images.unsplash.com/photo-1542291026-7eec264c27ff?w=500&h=600&fit=crop' },
                            { name: 'Street Tech Hoodie', price: '₹4,499', badge: 'NEW', image: 'https://images.unsplash.com/photo-1556821840-3a63f95609a7?w=500&h=600&fit=crop' },
                            { name: 'Training Shorts Pro', price: '₹2,499', badge: 'BESTSELLER', image: 'https://images.unsplash.com/photo-1591195853828-11db59a44f6b?w=500&h=600&fit=crop' },
                            { name: 'Performance Tee', price: '₹1,999', originalPrice: '₹2,999', badge: 'SALE', image: 'https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?w=500&h=600&fit=crop' },
                            { name: 'Basketball High-Tops', price: '₹11,999', badge: 'LIMITED', image: 'https://images.unsplash.com/photo-1579338559194-a162d19bf842?w=500&h=600&fit=crop' },
                            { name: 'Compression Leggings', price: '₹3,499', image: 'https://images.unsplash.com/photo-1506629082955-511b1aa562c8?w=500&h=600&fit=crop' },
                            { name: 'Windbreaker Jacket', price: '₹5,999', badge: 'NEW', image: 'https://images.unsplash.com/photo-1591047139829-d91aecb6caea?w=500&h=600&fit=crop' },
                            { name: 'Training Backpack', price: '₹3,999', image: 'https://images.unsplash.com/photo-1553062407-98eeb64c6a62?w=500&h=600&fit=crop' }
                        ].map((product, index) => (
                            <div
                                key={index}
                                onClick={() => navigate('/template2/shop')}
                                className="group cursor-pointer"
                            >
                                {/* Product Image */}
                                <div className="relative aspect-[4/5] overflow-hidden bg-zinc-900 mb-4">
                                    <img
                                        src={product.image}
                                        alt={product.name}
                                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                                    />

                                    {/* Badge */}
                                    {product.badge && (
                                        <div className={`absolute top-3 left-3 px-3 py-1 text-xs font-bold uppercase tracking-wider ${
                                            product.badge === 'SALE' ? 'bg-red-500' :
                                            product.badge === 'NEW' ? 'bg-green-500' :
                                            product.badge === 'LIMITED' ? 'bg-purple-500' :
                                            'bg-orange-500'
                                        }`}>
                                            {product.badge}
                                        </div>
                                    )}

                                    {/* Quick Add */}
                                    <div className="absolute bottom-0 left-0 right-0 p-4 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                                        <button className="w-full bg-white text-black py-3 text-sm font-bold uppercase tracking-wide hover:bg-gray-200 transition-colors">
                                            Quick Add
                                        </button>
                                    </div>
                                </div>

                                {/* Product Info */}
                                <h3 className="text-white text-sm lg:text-base font-medium mb-2 group-hover:text-red-500 transition-colors">
                                    {product.name}
                                </h3>
                                <div className="flex items-center gap-2">
                                    <span className="text-white font-bold">{product.price}</span>
                                    {product.originalPrice && (
                                        <span className="text-gray-500 line-through text-sm">{product.originalPrice}</span>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ============================================
                ATHLETE SHOWCASE - Feature banner
            ============================================ */}
            <section className="relative min-h-[70vh] flex items-center overflow-hidden">
                <div className="absolute inset-0">
                    <img
                        src="https://images.unsplash.com/photo-1517836357463-d25dfeac3438?w=1920&q=80"
                        alt="Athlete"
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-black via-black/70 to-transparent"></div>
                </div>

                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
                    <div className="max-w-xl">
                        <p className="text-red-500 font-bold uppercase tracking-widest mb-4">Athlete Spotlight</p>
                        <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black uppercase leading-tight mb-6">
                            "Greatness is earned, never given."
                        </h2>
                        <p className="text-gray-300 text-lg mb-8">
                            Join thousands of athletes who trust our gear to push beyond their limits.
                            Every product is tested by professionals, built for champions.
                        </p>
                        <button
                            onClick={() => navigate('/template2/shop')}
                            className="group inline-flex items-center gap-3 bg-red-600 text-white px-8 py-4 font-bold uppercase tracking-wide hover:bg-red-700 transition-all duration-300"
                        >
                            Shop Athlete Collection
                            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                        </button>
                    </div>
                </div>
            </section>

            {/* ============================================
                FEATURES - Trust badges
            ============================================ */}
            <section className="py-16 bg-zinc-950 border-y border-zinc-800">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
                        {[
                            { icon: Truck, title: 'Free Shipping', desc: 'On orders over ₹2,999' },
                            { icon: Shield, title: '2 Year Warranty', desc: 'Quality guaranteed' },
                            { icon: Clock, title: 'Easy Returns', desc: '30-day return policy' },
                            { icon: Zap, title: 'Fast Delivery', desc: '2-5 business days' }
                        ].map((feature, index) => (
                            <div key={index} className="text-center">
                                <div className="inline-flex items-center justify-center w-12 h-12 bg-zinc-800 rounded-full mb-4">
                                    <feature.icon className="w-6 h-6 text-red-500" />
                                </div>
                                <h3 className="text-white font-bold mb-1">{feature.title}</h3>
                                <p className="text-gray-500 text-sm">{feature.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ============================================
                BRAND VALUES - What we stand for
            ============================================ */}
            <section className="py-16 sm:py-20 lg:py-24 bg-black">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
                        {/* Image Grid */}
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-4">
                                <div className="aspect-[4/5] overflow-hidden">
                                    <img
                                        src="https://images.unsplash.com/photo-1526506118085-60ce8714f8c5?w=400&h=500&fit=crop"
                                        alt="Training"
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                                <div className="aspect-square overflow-hidden">
                                    <img
                                        src="https://images.unsplash.com/photo-1574680096145-d05b474e2155?w=400&h=400&fit=crop"
                                        alt="Detail"
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                            </div>
                            <div className="space-y-4 pt-8">
                                <div className="aspect-square overflow-hidden">
                                    <img
                                        src="https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?w=400&h=400&fit=crop"
                                        alt="Athlete"
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                                <div className="aspect-[4/5] overflow-hidden">
                                    <img
                                        src="https://images.unsplash.com/photo-1581009146145-b5ef050c149a?w=400&h=500&fit=crop"
                                        alt="Performance"
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Content */}
                        <div>
                            <p className="text-red-500 font-bold uppercase tracking-widest mb-4">Our Mission</p>
                            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black uppercase leading-tight mb-6">
                                Built for Those Who Never Quit
                            </h2>
                            <p className="text-gray-400 text-lg mb-8 leading-relaxed">
                                We're not just a brand – we're a movement. Every stitch, every fabric, every design
                                is engineered with one goal: to help you perform at your peak. From the gym to the
                                streets, our gear moves with you.
                            </p>

                            <div className="space-y-4 mb-8">
                                {[
                                    'Premium performance materials',
                                    'Designed by athletes, for athletes',
                                    'Sustainable manufacturing practices',
                                    'Community of 50,000+ athletes'
                                ].map((point, index) => (
                                    <div key={index} className="flex items-center gap-3">
                                        <div className="w-2 h-2 bg-red-500"></div>
                                        <span className="text-gray-300">{point}</span>
                                    </div>
                                ))}
                            </div>

                            <button
                                onClick={() => navigate('/template2/about')}
                                className="inline-flex items-center gap-2 text-white font-bold uppercase tracking-wide border-b-2 border-white pb-1 hover:text-red-500 hover:border-red-500 transition-all"
                            >
                                Our Story <ArrowRight className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            {/* ============================================
                NEWSLETTER - Join the squad
            ============================================ */}
            <section className="py-16 sm:py-20 lg:py-24 bg-gradient-to-r from-red-600 via-orange-600 to-yellow-500">
                <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black uppercase mb-4">
                        Join the Squad
                    </h2>
                    <p className="text-white/90 text-lg mb-8">
                        Get exclusive drops, early access, and 15% off your first order.
                    </p>
                    <form className="flex flex-col sm:flex-row gap-4 max-w-lg mx-auto">
                        <input
                            type="email"
                            placeholder="Enter your email"
                            className="flex-1 px-6 py-4 bg-white/20 border-2 border-white/30 text-white placeholder-white/60 focus:outline-none focus:border-white transition-colors"
                        />
                        <button
                            type="submit"
                            className="px-8 py-4 bg-black text-white font-bold uppercase tracking-wide hover:bg-zinc-900 transition-colors"
                        >
                            Subscribe
                        </button>
                    </form>
                    <p className="text-white/70 text-sm mt-4">
                        No spam. Unsubscribe anytime.
                    </p>
                </div>
            </section>

            {/* ============================================
                INSTAGRAM - Social proof
            ============================================ */}
            <section className="py-12 bg-black">
                <div className="text-center mb-8">
                    <p className="text-gray-400 text-sm uppercase tracking-widest">@yourbrandname</p>
                </div>
                <div className="grid grid-cols-3 md:grid-cols-6 gap-1">
                    {[
                        'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=300&h=300&fit=crop',
                        'https://images.unsplash.com/photo-1517836357463-d25dfeac3438?w=300&h=300&fit=crop',
                        'https://images.unsplash.com/photo-1526506118085-60ce8714f8c5?w=300&h=300&fit=crop',
                        'https://images.unsplash.com/photo-1574680096145-d05b474e2155?w=300&h=300&fit=crop',
                        'https://images.unsplash.com/photo-1581009146145-b5ef050c149a?w=300&h=300&fit=crop',
                        'https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?w=300&h=300&fit=crop'
                    ].map((image, index) => (
                        <div key={index} className="relative aspect-square overflow-hidden group cursor-pointer">
                            <img
                                src={image}
                                alt={`Instagram ${index + 1}`}
                                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                            />
                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/50 transition-colors flex items-center justify-center">
                                <svg className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                                </svg>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

        </div>
    );
};

export default Template2;
